import ApisServiceApp from './ApisServiceApp'
import { apiRequest, parseListQuery } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type ApiListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type { IAuditFieldChange, IAuditLog } from '@/interfaces/auditInterfaces'
import { AUDIT_PHAM_VI_TAT_CA } from '@/constants/chucNangs/auditChucNangs'

const AUDIT_BASE = '/audit-log'

function mapAuditFieldChanges(raw: unknown): IAuditFieldChange[] | undefined {
  if (!Array.isArray(raw)) return undefined
  return raw.map((item) => {
    const change = item as Record<string, unknown>
    return {
      field: String(change.field ?? ''),
      displayName: String(change.displayName ?? change.field ?? ''),
      oldValue: change.oldValue == null ? null : String(change.oldValue),
      newValue: change.newValue == null ? null : String(change.newValue),
    }
  })
}

function optionalString(value: unknown): string | undefined {
  return value != null && String(value).trim() !== '' ? String(value) : undefined
}

function optionalNumber(value: unknown): number | null {
  const n = Number(value)
  return value != null && Number.isFinite(n) ? n : null
}

export function mapAuditLog(raw: Record<string, unknown>): IAuditLog {
  const user = raw.user as { username?: string } | undefined
  const chiNhanh = raw.chiNhanh as { id?: unknown; ma?: unknown; ten?: unknown } | null | undefined
  const duAn = raw.duAn as { id?: unknown; ma?: unknown; ten?: unknown } | null | undefined
  return {
    id: raw.id as IAuditLog['id'],
    userId: raw.userId as IAuditLog['userId'],
    action: String(raw.action ?? ''),
    entityType: String(raw.entityType ?? ''),
    entityId: String(raw.entityId ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    fieldChanges: mapAuditFieldChanges(raw.fieldChanges),
    data: raw.data,
    oldValue: raw.oldValue,
    newValue: raw.newValue,
    ipAddress: optionalString(raw.ipAddress),
    userAgent: optionalString(raw.userAgent),
    phuongThuc: optionalString(raw.phuongThuc),
    duongDan: optionalString(raw.duongDan),
    truyVan: optionalString(raw.truyVan),
    tenApi: optionalString(raw.tenApi),
    hamXuLy: optionalString(raw.hamXuLy),
    nguon: optionalString(raw.nguon),
    ungDung: optionalString(raw.ungDung),
    phienBanApp: optionalString(raw.phienBanApp),
    manHinh: optionalString(raw.manHinh),
    loaiThietBi: optionalString(raw.loaiThietBi),
    heDieuHanh: optionalString(raw.heDieuHanh),
    trinhDuyet: optionalString(raw.trinhDuyet),
    tenThietBi: optionalString(raw.tenThietBi),
    maThietBi: optionalString(raw.maThietBi),
    duAnId: optionalNumber(raw.duAnId),
    chiNhanhId: optionalNumber(raw.chiNhanhId),
    chiNhanh:
      chiNhanh && chiNhanh.id != null
        ? { id: Number(chiNhanh.id), ma: optionalString(chiNhanh.ma), ten: optionalString(chiNhanh.ten) }
        : null,
    duAn:
      duAn && duAn.id != null
        ? { id: Number(duAn.id), ma: optionalString(duAn.ma), ten: optionalString(duAn.ten) }
        : null,
    createdAt: String(raw.createdAt ?? raw.ngayTao ?? ''),
    userName: raw.userName != null ? String(raw.userName) : user?.username,
    userFullName: optionalString(raw.userFullName),
  }
}

export const getListAuditLogs = async (querySt?: string): Promise<PagedListResult<IAuditLog>> => {
  const q = parseListQuery(querySt) as Record<string, unknown>
  const tatCaMang = String(q.phamVi ?? '') === AUDIT_PHAM_VI_TAT_CA
  const endpoint = tatCaMang ? 'danh-sach-audit-log-tat-ca-mang' : 'danh-sach-audit-log'
  const params = {
    ...(tatCaMang && q.duAnId ? { duAnId: String(q.duAnId) } : {}),
    page: Number(q.page) > 0 ? Number(q.page) : 1,
    pageSize: Number(q.pageSize) > 0 ? Number(q.pageSize) : 25,
    ...(q.entityType ? { entityType: String(q.entityType) } : {}),
    ...(q.entityId ? { entityId: String(q.entityId) } : {}),
    ...(q.userId ? { userId: Number(q.userId) } : {}),
    ...(q.chiNhanhId ? { chiNhanhId: String(q.chiNhanhId) } : {}),
    ...(q.action ? { action: String(q.action) } : {}),
    ...(q.nhomHanhDong ? { nhomHanhDong: String(q.nhomHanhDong) } : {}),
    ...(q.ipAddress ? { ipAddress: String(q.ipAddress) } : {}),
    ...(q.thietBi ? { thietBi: String(q.thietBi) } : {}),
    ...(q.duongDan ? { duongDan: String(q.duongDan) } : {}),
    ...(q.phuongThuc ? { phuongThuc: String(q.phuongThuc) } : {}),
    ...(q.nguon ? { nguon: String(q.nguon) } : {}),
    ...(q.manHinh ? { manHinh: String(q.manHinh) } : {}),
    ...(q.fromDate ? { fromDate: String(q.fromDate) } : {}),
    ...(q.toDate ? { toDate: String(q.toDate) } : {}),
    ...(q.search ? { search: String(q.search) } : {}),
    ...(q.ma ? { ma: String(q.ma) } : {}),
    ...(q.sortField ? { sortField: String(q.sortField) } : {}),
    ...(q.sortDirection ? { sortDirection: String(q.sortDirection) } : {}),
  }
  const res = (await apiRequest(
    ApisServiceApp.get(`${AUDIT_BASE}/${endpoint}`, { params }),
  )) as ApiListResponse<Record<string, unknown>> | Record<string, unknown>[]
  const normalized = normalizeListResponse<Record<string, unknown>>(res)
  return {
    ...normalized,
    data: normalized.data.map(mapAuditLog),
  }
}

/** `GET /audit-log/danh-sach-entity-type` — đồng bộ filter với ENTITY_TYPE_CONFIG BE. */
export const getDanhSachEntityType = async (): Promise<string[]> => {
  const res = await apiRequest(ApisServiceApp.get(`${AUDIT_BASE}/danh-sach-entity-type`))
  const raw = normalizeDetailResponse<unknown>(res as unknown[] | { data?: unknown })
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => String(item ?? '').trim())
    .filter((item) => item !== '')
}

/** `GET /audit-log/danh-sach-du-an-audit-log` — chỉ gọi khi có quyền xem tất cả mảng. */
export const getDanhSachDuAnAudit = async (): Promise<Array<{ id: number; ma: string; ten: string }>> => {
  const res = await apiRequest(ApisServiceApp.get(`${AUDIT_BASE}/danh-sach-du-an-audit-log`))
  const raw = normalizeDetailResponse<unknown>(res as unknown[] | { data?: unknown })
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => item as { id?: unknown; ma?: unknown; ten?: unknown })
    .filter((item) => item.id != null)
    .map((item) => ({ id: Number(item.id), ma: String(item.ma ?? ''), ten: String(item.ten ?? '') }))
}

export const getAuditHistoryByEntity = async (entityType: string, entityId: string | number): Promise<IAuditLog[]> => {
  const res = await apiRequest(
    ApisServiceApp.get(`${AUDIT_BASE}/${entityType}/${entityId}/lich-su-audit-log`),
  )
  const raw = normalizeDetailResponse<Record<string, unknown>[]>(
    res as Record<string, unknown>[] | { data?: Record<string, unknown>[] },
  )
  return Array.isArray(raw) ? raw.map(mapAuditLog) : []
}
